<template>
  <form class="loan-form" @submit.prevent="submit">
    <app-form-group :label="$t('finance.amount')" required>
      <app-fin-number
        v-model="amount"
        placeholder="0"
        inputmode="numeric"
        :error="$v.amount.$error"
        @input="invalidatePreview"
      />
      <template #error>
        <div v-if="$v.amount.$dirty && !$v.amount.required">{{ $t('common.required') }}</div>
        <div v-else-if="$v.amount.$dirty && !$v.amount.minValue">{{ $t('finance.positiveAmount') }}</div>
      </template>
    </app-form-group>
    <app-form-group :label="$t('finance.installments')" required>
      <div class="loan-form__installments">
        <button
          v-for="number in installmentsOptions"
          :key="number"
          type="button"
          :class="{ 'loan-form__installment--active': installments === number }"
          @click="selectInstallments(number)"
        >
          {{ number }}
        </button>
      </div>
    </app-form-group>
    <app-form-group :label="$t('common.note')" required>
      <app-textarea v-model="notes" :placeholder="$t('loans.notePlaceholder')" :error="$v.notes.$error" />
      <template #error><div v-if="$v.notes.$dirty && !$v.notes.required">{{ $t('common.required') }}</div></template>
    </app-form-group>
    <app-button ref="calculate" @click.native="calculate">{{ $t('common.calculate') }}</app-button>

    <template v-if="preview">
      <div class="loan-form__divider"></div>
      <div class="loan-form__eligibility" :class="{ 'loan-form__eligibility--error': !preview.eligible }">
        <strong>{{ preview.eligible ? $t('common.available') : $t('common.notAvailable') }}</strong> &mdash; {{ preview.reason.name }}
      </div>
      <cabinet-rows :rows="previewRows" />
      <h3 class="loan-form__section-title">{{ $t('common.calculation') }}</h3>
      <cabinet-rows class="loan-form__details" :rows="detailRows" />
      <h3 class="loan-form__section-title">{{ $t('common.scheduleCount', { count: preview.schedule.length }) }}</h3>
      <div class="loan-form__schedule">
        <div v-for="item in preview.schedule" :key="item.sequence">
          <span>{{ formatMonth(item.month) }}</span>
          <strong>{{ formatMoney(item.amount, preview.currency) }}</strong>
          <small>{{ $t('loans.schedulePrincipal', { principal: formatMoney(item.principal_amount, preview.currency), interest: formatMoney(item.interest_amount, preview.currency) }) }}</small>
        </div>
      </div>
      <app-button
        v-if="preview.eligible && !$v.$invalid"
        ref="submit"
        class="btn--stretch loan-form__submit"
        theme="request"
        type="submit"
      >
        {{ $t('common.submitRequest') }}
      </app-button>
    </template>
  </form>
</template>

<script>
import { getPersonalCabinetStaffLoanPreview, postPersonalCabinetStaffLoan } from '@/http'
import { between, minValue, required } from 'vuelidate/lib/validators'
import CabinetRows from './CabinetRows'
import { formatMoney, formatMonth, formatPercent } from './cabinetDisplay'

export default {
  name: 'LoanRequestForm',
  components: { CabinetRows },
  data() {
    return {
      amount: null,
      installments: 2,
      notes: null,
      preview: null,
      validationAttempted: false,
    }
  },
  validations: {
    amount: { required, minValue: minValue(1) },
    installments: { required, between: between(2, 6) },
    notes: { required },
  },
  computed: {
    policy() {
      return this.$store.state.personalCabinetData?.employee?.staff_loan_eligibility?.policy || {}
    },
    installmentsOptions() {
      const minimum = Number(this.policy.min_installments) || 2
      const maximum = Number(this.policy.max_installments) || 6
      return Array.from({ length: maximum - minimum + 1 }, (value, index) => index + minimum)
    },
    previewRows() {
      return [
        { label: this.$t('finance.principal'), value: formatMoney(this.preview.normalized_amount, this.preview.currency) },
        { label: this.$t('finance.interest', { percent: this.preview.interest_percent }), value: formatMoney(this.preview.interest_amount, this.preview.currency) },
        { label: this.$t('finance.totalRepayment'), value: formatMoney(this.preview.total_repayment_amount, this.preview.currency) },
        { label: this.$t('finance.monthlyPayment'), value: formatMoney(this.preview.monthly_payment, this.preview.currency) },
        { label: this.$t('finance.firstDeduction'), value: formatMonth(this.preview.first_payroll_month) },
        { label: this.$t('finance.expectedSalaryAfter'), value: formatMoney(this.preview.accrued_salary_after, this.preview.currency) },
      ]
    },
    detailRows() {
      return [
        { label: this.$t('finance.grossSalary'), value: formatMoney(this.preview.gross_salary, this.preview.currency) },
        { label: this.$t('finance.netSalaryBeforeDebt'), value: formatMoney(this.preview.net_salary_before_debt, this.preview.currency) },
        { label: this.$t('finance.availableMonthlyDeduction'), value: formatMoney(this.preview.available_monthly_deduction, this.preview.currency) },
        { label: this.$t('finance.salaryPercentAfter'), value: formatPercent(this.preview.accrued_salary_percent_after) },
      ]
    },
  },
  methods: {
    formatMoney,
    formatMonth,
    invalidatePreview() {
      this.preview = null
    },
    selectInstallments(number) {
      this.installments = number
      this.invalidatePreview()
    },
    calculate() {
      this.$v.$touch()
      const amount = Number(this.amount)
      if (this.$v.$invalid) return
      this.$refs.calculate.preload = true
      getPersonalCabinetStaffLoanPreview({ amount, installments: this.installments })
        .then(response => {
          this.preview = response.data
        })
        .finally(() => {
          this.$refs.calculate.preload = false
        })
    },
    submit() {
      this.validationAttempted = true
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$notify({
          type: 'info',
          title: this.$t('common.formInvalid'),
        })
        return
      }
      if (!this.preview?.eligible) return
      this.$refs.submit.preload = true
      postPersonalCabinetStaffLoan({
        amount: Number(this.amount),
        installments: this.installments,
        employee_notes: this.notes.trim(),
      })
        .then(() => {
          this.$emit('created')
        })
        .finally(() => {
          if (this.$refs.submit) this.$refs.submit.preload = false
        })
    },
  },
}
</script>

<style lang="sass" scoped>
.loan-form__installments
  display: grid
  grid-template-columns: repeat(6, minmax(0, 1fr))
  gap: 8px

  button
    height: 42px
    border: 1px solid #ddd
    border-radius: 9px
    color: #555
    font-weight: 600

  .loan-form__installment--active
    border-color: $color-theme
    background-color: #f2f9e7
    color: #4f8d12

.loan-form__divider
  margin: 18px 0
  border-top: 1px solid #ededed

.loan-form__eligibility
  padding: 13px 15px
  border-radius: 9px
  background-color: #eff8e9
  color: #4f8d12

.loan-form__eligibility--error
  background-color: #fff0f0
  color: #c63b3b

.loan-form__section-title
  margin-top: 14px
  color: #303126
  font-size: 17px
  font-weight: 700

.loan-form__details,
.loan-form__schedule
  margin-top: 10px
  padding: 10px 14px
  border-radius: 10px
  background-color: #f8f8f8

.loan-form__schedule > div
  display: grid
  grid-template-columns: 1fr auto
  gap: 4px 14px
  padding: 10px 0
  border-bottom: 1px solid #e9e9e9
  color: #808080

  &:last-child
    border-bottom: 0

  strong
    color: #3d3d3d
    font-weight: 500

  small
    grid-column: 1 / -1
    color: #999
    font-size: 10px

.loan-form__submit
  margin-top: 14px
</style>
